import { Icon } from '../../../../layout/Icon.jsx';
import { Pill } from '../../../../layout/Pill.jsx';
import { useAuditEntry } from '../../hooks/useAudit.js';
import {
  labelForAction,
  labelForModule,
  labelForSeverity,
  variantForSeverity,
  hasActionLabel,
  formatAuditDateTime
} from './auditLabels.js';

function JsonBlock({ value, empty = '—' }) {
  if (value == null) return <div className="text-ink-3 text-[12px]">{empty}</div>;
  const text = typeof value === 'string' ? value : JSON.stringify(value, null, 2);
  return (
    <pre
      dir="ltr"
      className="text-[11.5px] font-mono whitespace-pre-wrap p-3 bg-paper-2 rounded-[8px] border border-line max-h-[280px] overflow-auto text-start"
    >
      {text}
    </pre>
  );
}

export function AuditDetailModal({ open, entryId, fallback, onClose }) {
  const { data, isLoading } = useAuditEntry(open ? entryId : null);
  if (!open) return null;
  const e = data || fallback;
  const sev = variantForSeverity(e?.severity);

  return (
    <div
      className="fixed inset-0 z-50 grid place-items-center p-4"
      style={{ background: 'rgba(0,0,0,.45)' }}
      dir="rtl"
    >
      <div
        className="card w-[760px] max-w-full max-h-[88vh] flex flex-col"
        onClick={(ev) => ev.stopPropagation()}
      >
        <div className="card-h">
          <h3 className="text-[15px] font-bold flex-1">تفاصيل الإجراء</h3>
          <button type="button" className="btn btn-ghost" onClick={onClose}>
            <Icon name="x" size={14} />
          </button>
        </div>
        <div className="card-b overflow-auto space-y-3">
          {isLoading && !e ? (
            <div className="text-ink-3 text-center py-8">جاري التحميل...</div>
          ) : !e ? (
            <div className="text-ink-3 text-center py-8">لا توجد بيانات</div>
          ) : (
            <>
              <div className="grid grid-cols-2 gap-3 text-[13px]">
                <div>
                  <div className="text-[11px] text-ink-3">التاريخ والوقت</div>
                  <div className="font-semibold">{formatAuditDateTime(e.createdAt || e.timestamp)}</div>
                </div>
                <div>
                  <div className="text-[11px] text-ink-3">المستخدم</div>
                  <div className="font-bold">
                    {e.userName || e.user?.fullName || e.user?.username
                      || e.userId?.fullName || e.userId?.username || 'النظام'}
                  </div>
                </div>
                <div>
                  <div className="text-[11px] text-ink-3">الإجراء</div>
                  <div className="font-bold">{labelForAction(e.action, e.module)}</div>
                  {e.action && !hasActionLabel(e.action) ? (
                    <div className="text-[11px] text-ink-3 font-mono" dir="ltr">{e.action}</div>
                  ) : null}
                </div>
                <div>
                  <div className="text-[11px] text-ink-3">الوحدة</div>
                  <div>{labelForModule(e.module)}</div>
                </div>
                <div>
                  <div className="text-[11px] text-ink-3">الخطورة</div>
                  <Pill variant={sev}>{labelForSeverity(e.severity)}</Pill>
                </div>
                <div>
                  <div className="text-[11px] text-ink-3">رقم السجل</div>
                  <div className="font-mono text-[12px]" dir="ltr">{e.recordId || '—'}</div>
                </div>
                <div>
                  <div className="text-[11px] text-ink-3">عنوان IP</div>
                  <div className="font-mono text-[12px]" dir="ltr">{e.ipAddress || e.ip || '—'}</div>
                </div>
                <div>
                  <div className="text-[11px] text-ink-3">المتصفح</div>
                  <div className="text-[12px] truncate" dir="ltr">{e.userAgent || '—'}</div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <div className="text-[12px] font-bold mb-1.5 text-ink-2">القيمة قبل التعديل</div>
                  <JsonBlock value={e.oldValue ?? e.before} />
                </div>
                <div>
                  <div className="text-[12px] font-bold mb-1.5 text-ink-2">القيمة بعد التعديل</div>
                  <JsonBlock value={e.newValue ?? e.after} />
                </div>
              </div>

              {e.message || e.details ? (
                <div>
                  <div className="text-[12px] font-bold mb-1.5 text-ink-2">ملاحظات</div>
                  <div className="text-[12.5px] text-ink">{e.message || e.details}</div>
                </div>
              ) : null}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
